/*
 * @providesModule GradientLabel
 *
 */

import React, {useMemo, useRef} from 'react';
import {StyleProp, TextStyle, StyleSheet, View, Text} from 'react-native';

type GradientStyle = TextStyle & {
  strokeColor?: string;
  color?: string;
  gradientAngle?: number;
  gradientColors?: string[];
  strokeSize?: string;
};

interface GradientLabelProps {
  style?: StyleProp<GradientStyle>;
  children: string;
}

export default function GradientLabel(props: GradientLabelProps) {
  const {style} = props;
  const mergedStyles = useMemo(() => {
    const flatten = StyleSheet.flatten(style);

    if (!flatten.color) {
      flatten.color = flatten.strokeColor || flatten.gradientColors?.[0];
    }

    const {
      gradientColors,
      gradientAngle,
      textShadowRadius,
      textShadowColor,
      textShadowOffset,
      strokeColor,
      strokeSize,
      ...defaultStyles
    } = flatten;

    const gradientStyles: Record<string, string> = {};
    if (gradientColors?.length) {
      const angle = gradientAngle ?? 0;
      const colors = gradientColors.join(', ');

      gradientStyles.backgroundImage = `linear-gradient(${angle}deg, ${colors})`;
      gradientStyles.backgroundClip = 'text';
      gradientStyles.textFillColor = 'transparent';
      gradientStyles.WebkitTextFillColor = 'transparent';
      gradientStyles.MozTextFillColor = 'transparent';
    }

    const shadowStyles: Record<string, string> = {};
    if (textShadowColor) {
      const x = textShadowOffset?.width ?? 0;
      const y = textShadowOffset?.height ?? 0;
      const radius = textShadowRadius ?? 0;

      shadowStyles.overflow = 'visible';
      shadowStyles.filter = `drop-shadow(${x}px ${y}px ${radius}px ${textShadowColor as string})`;
    }

    const strokeStyles: Record<string, string> = {};
    if (strokeSize && strokeColor) {
      strokeStyles.textStroke = `${strokeSize || 1}px ${strokeColor}`;
      strokeStyles['-webkit-text-stroke'] = `${strokeSize || 1}px ${strokeColor}`;
    }

    return {...defaultStyles, ...gradientStyles, ...shadowStyles, ...strokeStyles};
  }, [style]);

  return (
    <Text
      {...props}
      numberOfLines={1}
      adjustsFontSizeToFit={true}
      minimumFontScale={0.1}
      allowFontScaling={false}
      style={mergedStyles}
    />
  );
}
